import dayjs, { extend as dayjsExtend } from 'dayjs'
import utc from 'dayjs/plugin/utc'

dayjsExtend(utc)

const SECOND = 1000
const MINUTE = SECOND * 60
const HOUR = MINUTE * 60
const DAY = HOUR * 24
const WEEK = DAY * 7
const MONTH = DAY * 30
const YEAR = DAY * 365

export enum DateUnit {
  None = Infinity,
  Second = SECOND,
  Minute = MINUTE,
  Hour = HOUR,
  Day = DAY,
  Week = WEEK,
  Month = MONTH,
  Year = YEAR,
}

export const DateUnitMap = {
  [DateUnit.Second]: 'second',
  [DateUnit.Minute]: 'min',
  [DateUnit.Hour]: 'hr',
  [DateUnit.Day]: 'day',
  [DateUnit.Week]: 'week',
  [DateUnit.Month]: 'month',
  [DateUnit.Year]: 'year',
}

const DATE_UNIT = Object.keys(DateUnit)
  .filter(u => !isNaN(+u))
  .map(u => +u)
  .sort((a, b) => b - a)

export const getDateIntervalByUnit = (date: Date, now = Date.now()) => {
  const diffTimeStamp = now - date.getTime()
  let unit: DateUnit = DateUnit.None
  let value = 0
  DATE_UNIT.some(u => {
    if (diffTimeStamp >= u) {
      unit = u
      value = Math.floor(diffTimeStamp / u)
      return true
    }
  })
  return {
    unit,
    value,
  } as { unit: DateUnit; value: number }
}

export const getPluralDateIntervalsByUnit = (date: Date, now = Date.now()) => {
  const { unit, value } = getDateIntervalByUnit(date, now)
  return {
    unit: unit === DateUnit.None ? 'now' : value > 1 ? DateUnitMap[unit] + 's' : DateUnitMap[unit],
    value,
  }
}

export const parseTimeAgo = (date: string | number | Date, toGMT = true, now = Date.now()) => {
  if (toGMT && typeof date === 'string' && !date.match(/Z$/)) {
    date += 'Z'
  }
  date = new Date(date)
  const { unit, value } = getDateIntervalByUnit(date, now)
  if (unit === DateUnit.Year) {
    return {
      value: new Intl.DateTimeFormat('en-US', {
        dateStyle: 'long',
      }).format(date),
    }
  }
  if (
    unit === DateUnit.None ||
    unit === DateUnit.Second ||
    (unit === DateUnit.Minute && value <= 2)
  ) {
    return {
      value: 'publish',
    }
  }
  return {
    unit: DateUnitMap[unit] + (value > 1 ? 's' : ''),
    value,
  }
}

// date time validation
// valid cases e.g. 11:00, 8:00
// invalid cases e.g. 11:0, 51:90
export function isClockTime(value: string) {
  if (!/^[0-9]{1,2}:[0-9]{2}$/.test(value)) {
    return false
  }
  const parts = value.split(':')

  if (Number(parts[0]) > 23) {
    return false
  }

  if (Number(parts[1]) > 59) {
    return false
  }

  return true
}

// parse the time like "11:00" to UTC timestamp
export function parseClockTime(baseDate: number, clockTime: string): number {
  const parts = clockTime.split(':')
  if (parts.length < 2) return dayjs(baseDate).utc(true).valueOf()
  return dayjs(baseDate).utc(true).hour(Number(parts[0])).minute(Number(parts[1])).valueOf()
}

export function timeStampToUTCString(timestamp: number, format = ''): string {
  return dayjs(timestamp).utc().format(format)
}

export function parseClockTimeToUTCString(baseDate: number, clockTime: string): string {
  const timestamp = parseClockTime(baseDate, clockTime)
  return timeStampToUTCString(timestamp)
}
